import React, { useEffect, useMemo, useState } from "react";
import StyledModal from "../../components/CustomModal";
import { Form, FormInstance, Input, Upload, UploadFile, Button, Space, Checkbox, Row, Col, Select } from "antd";
import { useAppSelector } from "../../utils/hooks/redux";
import { FooterButtons, FormWrapper, StyledButton } from "../../components/styles";
import { selectEstablishmentType } from "../../redux/features/establishmentType/establishmentTypeSlice";
import { CustomUploadFile } from "../../utils/helpers/validateUploadFiles";
import { UploadFileStatus } from "antd/es/upload/interface";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { SpecificType } from "../../types";

type ModalProps = {
    isModalOpen: boolean;
    handleSave: (values: any) => void;
    onCancel: () => void;
    form: FormInstance<any>;
    editingRecord: any | null;
    setEditingRecord: any | null;
    hasAddPermission: boolean,
    hasEditPermission: boolean,
    hasViewPermission: boolean
};

const CreateEditModal: React.FC<ModalProps> = ({ isModalOpen, handleSave, onCancel, form, hasAddPermission, hasEditPermission, editingRecord, setEditingRecord }) => {
    const { loading } = useAppSelector(selectEstablishmentType);
    const canSaveChanges = useMemo(() => hasAddPermission || hasEditPermission, [hasAddPermission, hasEditPermission]);
    const [file, setFile] = useState<UploadFile | null>(null);
    const [heroImageFile, setHeroImageFile] = useState<UploadFile | null>(null);
    const [specificTypes, setSpecificTypes] = useState<SpecificType[]>([]);
    const enableLandingPage = Form.useWatch("enableLandingPage", form);
    const languageOptions = useMemo(
        () => [
            { code: "en", label: "English" },
            { code: "es", label: "Spanish" },
            { code: "fr", label: "French" },
            { code: "de", label: "German" },
            { code: "pt", label: "Portuguese" },
            { code: "ar", label: "Arabic" },
        ],
        []
    );

    const handlePreview = async (file: CustomUploadFile) => {
        if (file.url) {
            window.open(file.url, "_blank");
        }
    };

    const handleFileChange = async ({ fileList }: any) => {
        const currentFile = fileList.length > 0 ? fileList[0] : null;
        setFile(currentFile);
        if (currentFile && currentFile.originFileObj) {
            const realFile = currentFile.originFileObj as File;
            const blob = new Blob([realFile], { type: realFile.type });
            form.setFieldsValue({ icon: blob });
        } else {
            form.setFieldsValue({ icon: undefined });
            setEditingRecord({ ...editingRecord, fullUrl: undefined })
        }
    };

    const handleRemove = () => {
        setFile(null);
        form.setFieldsValue({ icon: undefined });
        setEditingRecord({ ...editingRecord, fullUrl: undefined })
        return true;
    };

    const handleHeroImageChange = async ({ fileList }: any) => {
        const currentFile = fileList.length > 0 ? fileList[0] : null;
        setHeroImageFile(currentFile);
        if (currentFile && currentFile.originFileObj) {
            const realFile = currentFile.originFileObj as File;
            const blob = new Blob([realFile], { type: realFile.type });
            form.setFieldsValue({
                heroContent: {
                    ...form.getFieldValue('heroContent'),
                    heroImageUrl: blob
                }
            });
        } else {
            const currentHeroContent = form.getFieldValue('heroContent') || {};
            delete currentHeroContent.heroImageUrl;
            form.setFieldsValue({ heroContent: currentHeroContent });
        }
    };

    const handleHeroImageRemove = () => {
        setHeroImageFile(null);
        const currentHeroContent = form.getFieldValue('heroContent') || {};
        delete currentHeroContent.heroImageUrl;
        form.setFieldsValue({ heroContent: currentHeroContent });
        return true;
    };

    useEffect(() => {
        if (editingRecord) {
            if (editingRecord?.fullUrl) {
                setFile({
                    uid: editingRecord?.id.toString(),
                    name: editingRecord?.fullUrl || 'Icon',
                    status: 'done' as UploadFileStatus,
                    url: editingRecord?.fullUrl,
                })
            } else {
                setFile(null)
            }
            // Load hero image if exists
            if (editingRecord?.heroContent?.heroImageUrl) {
                const heroName = (editingRecord?.heroContent?.heroImageUrl || '').split('/').pop() || 'Hero Image';
                setHeroImageFile({
                    uid: `${editingRecord?.id}-hero`,
                    name: heroName,
                    status: 'done' as UploadFileStatus,
                    url: editingRecord?.heroContent?.heroImageUrl,
                })
            } else {
                setHeroImageFile(null)
            }
            form.setFieldsValue({
                enableLandingPage: !!editingRecord?.heroContent,
                heroContent: editingRecord?.heroContent || {},
                availableLanguages: editingRecord?.availableLanguages && Array.isArray(editingRecord.availableLanguages)
                    ? Array.from(new Set([...(editingRecord.availableLanguages || []), "en"]))
                    : ['en'],
            });
            // Load specific types from editing record
            if (editingRecord?.specificTypes && Array.isArray(editingRecord.specificTypes)) {
                setSpecificTypes(editingRecord.specificTypes);
            } else {
                setSpecificTypes([]);
            }
        } else {
            setSpecificTypes([]);
            setHeroImageFile(null);
            form.setFieldsValue({ enableLandingPage: false, availableLanguages: ['en'] });
        }
    }, [editingRecord, form]);

    const handleAddSpecificType = () => {
        setSpecificTypes([...specificTypes, { name: '', isQuestionType: false }]);
    };

    const handleRemoveSpecificType = (index: number) => {
        const newTypes = specificTypes.filter((_, i) => i !== index);
        setSpecificTypes(newTypes);
        form.setFieldsValue({ specificTypes: newTypes });
    };

    const handleSpecificTypeNameChange = (index: number, name: string) => {
        const newTypes = [...specificTypes];
        newTypes[index] = { ...newTypes[index], name };
        setSpecificTypes(newTypes);
        form.setFieldsValue({ specificTypes: newTypes });
    };

    const handleQuestionTypeChange = (index: number, isQuestionType: boolean) => {
        const newTypes = [...specificTypes];
        newTypes[index] = { ...newTypes[index], isQuestionType };
        setSpecificTypes(newTypes);
        form.setFieldsValue({ specificTypes: newTypes });
    };

    // Sync specificTypes state with form whenever it changes
    useEffect(() => {
        form.setFieldsValue({ specificTypes });
    }, [specificTypes, form]);

    return (
        <StyledModal
            draggable={true}
            title={form.getFieldValue("id") ? `${canSaveChanges ? "Edit" : "View"} EstablishmentType` : `${canSaveChanges ? "Add" : "View"} EstablishmentType`}
            open={isModalOpen}
            closeIcon={null}
            footer={
                <FooterButtons>
                    <StyledButton onClick={onCancel} disabled={loading}>
                        Cancel
                    </StyledButton>
                    {canSaveChanges && <StyledButton
                        type="primary"
                        onClick={async () => {
                            try {
                                // Ensure specificTypes is in form values before validation
                                form.setFieldsValue({ specificTypes });
                                const values = await form.validateFields();
                                handleSave(values);
                            } catch (error) {
                                console.error("Validation failed:", error);
                            }
                        }}
                        loading={loading}
                    >
                        Save
                    </StyledButton>}
                </FooterButtons>
            }
            centered
            onCancel={() => !loading && onCancel()}
        >
            <FormWrapper
                form={form}
                layout="vertical"
                initialValues={{ enableLandingPage: false }}
            >
                <Form.Item label="Establishment Type Name" name="name" rules={[{ required: true, message: "Please enter Establishment Type name" }]}>
                    <Input size="large" disabled={loading || !canSaveChanges} />
                </Form.Item>

                <Form.Item
                    label="Specific Types"
                    name="specificTypes"
                    tooltip="Define the specific categories for this establishment type and mark which ones can be used as question types"
                >
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {specificTypes.map((type, index) => (
                            <Row key={index} gutter={8} align="middle">
                                <Col span={12}>
                                    <Input
                                        placeholder="Type name (e.g., Airport, Airline)"
                                        value={type.name}
                                        onChange={(e) => handleSpecificTypeNameChange(index, e.target.value)}
                                        disabled={loading || !canSaveChanges}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Checkbox
                                        checked={type.isQuestionType}
                                        onChange={(e) => handleQuestionTypeChange(index, e.target.checked)}
                                        disabled={loading || !canSaveChanges}
                                    >
                                        Is Question Type
                                    </Checkbox>
                                </Col>
                                <Col span={4}>
                                    <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => handleRemoveSpecificType(index)}
                                        disabled={loading || !canSaveChanges}
                                    />
                                </Col>
                            </Row>
                        ))}
                        <Button
                            type="dashed"
                            icon={<PlusOutlined />}
                            onClick={handleAddSpecificType}
                            disabled={loading || !canSaveChanges}
                            style={{ width: '100%' }}
                        >
                            Add Specific Type
                        </Button>
                    </Space>
                </Form.Item>

                <Form.Item label="Translation Key" name="translationKey" tooltip="Optional i18n key to map this establishment type across products">
                    <Input size="large" disabled={loading || !canSaveChanges} placeholder="e.g., landing.services.airportAirline" />
                </Form.Item>

                <div style={{ marginBottom: 8, fontWeight: 600 }}>Localized Names</div>
                <Row gutter={[12, 12]}>
                    {languageOptions.map((lang) => (
                        <Col span={12} key={lang.code}>
                            <Form.Item name={["nameTranslations", lang.code]} label={lang.label}>
                                <Input
                                    size="large"
                                    disabled={loading || !canSaveChanges}
                                    placeholder={`Enter ${lang.label} name`}
                                />
                            </Form.Item>
                        </Col>
                    ))}
                </Row>

                <Form.Item label="Upload Icon (SVG, PNG, JPG, WEBP)" name="icon">
                    <Upload
                        name="icon"
                        listType="picture"
                        fileList={file ? [file] : []}
                        accept=".svg,.png,.jpg,.jpeg,.webp,.xml"
                        maxCount={1}
                        onPreview={handlePreview}
                        onChange={handleFileChange}
                        onRemove={handleRemove}
                        beforeUpload={(file) => {
                            const isAllowedType = [
                                "image/png",
                                "image/jpg",
                                "image/jpeg",
                                "image/webp",
                                "image/svg+xml",
                                "application/xml",
                            ].includes(file.type);

                            if (!isAllowedType) {
                                console.log('Invalid file type. Allowed: PNG, JPG, JPEG, WEBP, SVG, XML');
                                return Upload.LIST_IGNORE;
                            }

                            const isLt5MB = file.size / 1024 / 1024 < 5;
                            if (!isLt5MB) {
                                console.log('File must be smaller than 5MB!');
                                return Upload.LIST_IGNORE;
                            }

                            return false;
                        }}
                        disabled={loading || !canSaveChanges}
                    >
                        <StyledButton size="large" disabled={loading || !canSaveChanges}>
                            Click to Upload
                        </StyledButton>
                    </Upload>
                </Form.Item>

                <Form.Item
                    name="enableLandingPage"
                    valuePropName="checked"
                >
                    <Checkbox
                        disabled={loading || !canSaveChanges}
                        onChange={(e) => {
                            const checked = e.target.checked;
                            form.setFieldsValue({ enableLandingPage: checked });
                            if (!checked) {
                                setHeroImageFile(null);
                                form.setFieldsValue({
                                    heroContent: {},
                                    availableLanguages: undefined,
                                });
                            } else {
                                // Set default languages when enabling
                                form.setFieldsValue({
                                    availableLanguages: ['en', 'es']
                                });
                            }
                        }}
                    >
                        Enable Landing Page
                    </Checkbox>
                </Form.Item>
                {enableLandingPage && (
                    <Form.Item
                        label="Available Languages"
                        name="availableLanguages"
                        tooltip="Select which languages should be available for this establishment's landing page. English is always available."
                        initialValue={['en', 'es']}
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (!value || !value.includes('en')) {
                                        return Promise.reject('English must be selected');
                                    }
                                    if (!value || value.length === 0) {
                                        return Promise.reject('Please select at least one language');
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <Checkbox.Group
                            disabled={loading || !canSaveChanges}
                            onChange={(checkedValues) => {
                                // Ensure English is always included
                                if (!checkedValues.includes('en')) {
                                    const newValues = ['en', ...checkedValues];
                                    form.setFieldsValue({ availableLanguages: newValues });
                                }
                            }}
                        >
                            <Row gutter={[16, 16]}>
                                {languageOptions.map(lang => (
                                    <Col span={12} key={lang.code}>
                                        <Checkbox
                                            value={lang.code}
                                            disabled={loading || !canSaveChanges || lang.code === 'en'}
                                        >
                                            {lang.label} {lang.code === 'en' && '(Required)'}
                                        </Checkbox>
                                    </Col>
                                ))}
                            </Row>
                        </Checkbox.Group>
                    </Form.Item>
                )}



                {enableLandingPage && (
                    <Form.Item
                        label="URL Slug"
                        name="slug"
                        tooltip="URL-friendly identifier for landing pages (e.g., 'airportairline'). Lowercase only."
                        normalize={(value: string) => (value ? value.toLowerCase() : value)}
                    >
                        <Input size="large" disabled={loading || !canSaveChanges} placeholder="e.g., airportairline" />
                    </Form.Item>
                )}


                {enableLandingPage && (
                    <Row gutter={[12, 12]}>
                        <Col span={24}>
                            <Form.Item label="Hero Title" name={["heroContent", "title"]}>
                                <Input size="large" disabled={loading || !canSaveChanges} placeholder="e.g., Airport & Airline Lost and Found" />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item label="Hero Subtitle" name={["heroContent", "subtitle"]}>
                                <Input.TextArea rows={2} disabled={loading || !canSaveChanges} placeholder="e.g., Track your lost items from airports and airlines worldwide" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Badge Text" name={["heroContent", "badgeText"]}>
                                <Input size="large" disabled={loading || !canSaveChanges} placeholder="e.g., Trusted by Travelers" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Background Color" name={["heroContent", "backgroundColor"]}>
                                <Input size="large" disabled={loading || !canSaveChanges} placeholder="e.g., #003087" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Primary CTA Text" name={["heroContent", "primaryCtaText"]}>
                                <Input size="large" disabled={loading || !canSaveChanges} placeholder="e.g., File a Claim" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Primary CTA Link" name={["heroContent", "primaryCtaLink"]}>
                                <Input size="large" disabled={loading || !canSaveChanges} placeholder="e.g., /file-a-claim" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Secondary CTA Text" name={["heroContent", "secondaryCtaText"]}>
                                <Input size="large" disabled={loading || !canSaveChanges} placeholder="e.g., Learn More" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Secondary CTA Link" name={["heroContent", "secondaryCtaLink"]}>
                                <Input size="large" disabled={loading || !canSaveChanges} placeholder="e.g., /about" />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item label="Hero Image" name={["heroContent", "heroImageUrl"]}>
                                <Upload
                                    name="heroImage"
                                    listType="picture"
                                    fileList={heroImageFile ? [heroImageFile] : []}
                                    accept=".png,.jpg,.jpeg,.webp"
                                    maxCount={1}
                                    onPreview={handlePreview}
                                    onChange={handleHeroImageChange}
                                    onRemove={handleHeroImageRemove}
                                    beforeUpload={(file) => {
                                        const isAllowedType = [
                                            "image/png",
                                            "image/jpg",
                                            "image/jpeg",
                                            "image/webp",
                                        ].includes(file.type);

                                        if (!isAllowedType) {
                                            console.log('Invalid file type. Allowed: PNG, JPG, JPEG, WEBP');
                                            return Upload.LIST_IGNORE;
                                        }

                                        const isLt5MB = file.size / 1024 / 1024 < 5;
                                        if (!isLt5MB) {
                                            console.log('File must be smaller than 5MB!');
                                            return Upload.LIST_IGNORE;
                                        }

                                        return false;
                                    }}
                                    disabled={loading || !canSaveChanges}
                                >
                                    <StyledButton size="large" disabled={loading || !canSaveChanges}>
                                        Click to Upload Hero Image
                                    </StyledButton>
                                </Upload>
                            </Form.Item>
                        </Col>
                    </Row>
                )}

                {enableLandingPage && (
                    <>
                        <div style={{ margin: '12px 0 6px', fontWeight: 600 }}>Localized Hero Content</div>
                        <Row gutter={[12, 12]}>
                            {languageOptions.map((lang) => (
                                <Col span={12} key={lang.code}>
                                    <Form.Item label={`${lang.label} Title`} name={["heroContentTranslations", lang.code, "title"]}>
                                        <Input size="large" disabled={loading || !canSaveChanges} placeholder={`Hero Title (${lang.label})`} />
                                    </Form.Item>
                                </Col>
                            ))}
                            {languageOptions.map((lang) => (
                                <Col span={12} key={`${lang.code}-subtitle`}>
                                    <Form.Item label={`${lang.label} Subtitle`} name={["heroContentTranslations", lang.code, "subtitle"]}>
                                        <Input.TextArea rows={2} disabled={loading || !canSaveChanges} placeholder={`Hero Subtitle (${lang.label})`} />
                                    </Form.Item>
                                </Col>
                            ))}
                            {languageOptions.map((lang) => (
                                <Col span={12} key={`${lang.code}-primary`}>
                                    <Form.Item label={`${lang.label} Primary CTA`} name={["heroContentTranslations", lang.code, "primaryCtaText"]}>
                                        <Input size="large" disabled={loading || !canSaveChanges} placeholder={`Primary CTA (${lang.label})`} />
                                    </Form.Item>
                                </Col>
                            ))}
                            {languageOptions.map((lang) => (
                                <Col span={12} key={`${lang.code}-badge`}>
                                    <Form.Item label={`${lang.label} Badge`} name={["heroContentTranslations", lang.code, "badgeText"]}>
                                        <Input size="large" disabled={loading || !canSaveChanges} placeholder={`Badge (${lang.label})`} />
                                    </Form.Item>
                                </Col>
                            ))}
                        </Row>
                    </>
                )}
            </FormWrapper>
        </StyledModal>
    );
};

export default CreateEditModal;
